import type { Locale } from 'date-fns'
import { enGB as enGBDateFns, tr } from 'date-fns/locale'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import enGB from './locales/en_GB.json'
import trTR from './locales/tr_TR.json'

export type DateFormatConfig = {
  /** Date only, e.g. "15 Şub 2026" */
  short: string
  /** Date and time (24h for TR), e.g. "15 Şub 2026 16:54" */
  datetime: string
  /** Month and day for compact labels, e.g. "15 Şub" */
  monthDay: string
}

type LanguageConfig = {
  code: string
  name: string
  translation: Record<string, unknown>
  dateFnsLocale: Locale
  /** Override default date formats (optional). Defaults: short "MMM d, yyyy", datetime "MMM d, yyyy h:mm a", monthDay "MMM d" */
  dateFormats?: Partial<DateFormatConfig>
  flag?: string
}

export const DEFAULT_DATE_FORMATS: DateFormatConfig = {
  short: 'MMM d, yyyy',
  datetime: 'MMM d, yyyy h:mm a',
  monthDay: 'MMM d',
}

// Add new languages here - only 2 places to edit:
// 1. Add locale JSON import above
// 2. Add entry below (code, name, translation, dateFnsLocale, dateFormats)
// dateFormats: short = date only, datetime = date + time, monthDay = compact (e.g. scale labels)
export const LANGUAGES: LanguageConfig[] = [
  {
    code: 'en_GB',
    name: 'English',
    translation: enGB,
    dateFnsLocale: enGBDateFns,
    dateFormats: {
      short: 'MMM d, yyyy',           // e.g. Feb 15, 2026
      datetime: 'MMM d, yyyy h:mm a', // e.g. Feb 15, 2026 4:54 PM
      monthDay: 'MMM d',              // e.g. Feb 15
    },
  },
  {
    code: 'tr_TR',
    name: 'Türkçe',
    translation: trTR,
    dateFnsLocale: tr,
    dateFormats: {
      short: 'd MMM yyyy',           // e.g. 15 Şub 2026
      datetime: 'd MMM yyyy HH:mm',  // e.g. 15 Şub 2026 16:54
      monthDay: 'd MMM',             // e.g. 15 Şub
    },
  },
]

export const SUPPORTED_LANGUAGES: string[] = LANGUAGES.map((l) => l.code)
export type SupportedLanguage = (typeof LANGUAGES)[number]['code']

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> =
  LANGUAGES.reduce(
    (acc, lang) => {
      acc[lang.code as SupportedLanguage] = lang.name
      return acc
    },
    {} as Record<SupportedLanguage, string>
  )

// Build resources from LANGUAGES (translation only for i18next)
const resources: Record<string, { translation: Record<string, unknown> }> =
  LANGUAGES.reduce((acc, lang) => {
    acc[lang.code] = { translation: lang.translation }
    return acc
  }, {} as Record<string, { translation: Record<string, unknown> }>)

export const isI18nEnabled =
  import.meta.env.VITE_I18N_ENABLED === undefined ||
  import.meta.env.VITE_I18N_ENABLED === 'true'

const STORAGE_KEY = 'i18nextLng'
const DEFAULT_LANGUAGE: SupportedLanguage = 'en_GB'

function getInitialLanguage(): SupportedLanguage {
  if (!isI18nEnabled) return DEFAULT_LANGUAGE
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage
  }
  const osLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || ''
  const osLocale = osLang.split('-')[0].toLowerCase()
  if (osLocale === 'tr') return 'tr_TR'
  return DEFAULT_LANGUAGE
}

i18n.use(initReactI18next).init({
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  resources,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
})

export { i18n }
export function persistLanguage(lng: string): void {
  if (isI18nEnabled && typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, lng)
  }
}
